<style>
    .mtapp-add-row-button {
        top: 0.5rem;
    }
</style>

<script>
    import { createEventDispatcher } from 'svelte';
    import MultiFieldProInput from './MultiFieldProInput.svelte';
    import MultiFieldProAsset from './MultiFieldProAsset.svelte';
    import { deepCopy } from '../methods/deepCopy.js';
    import { modalConfirm } from '../methods/modalConfirm.js';

    export let mtappVars;
    export let id;
    export let storage;
    export let storageKey;
    export let handle;
    export let fieldBlocks;

    const dispatch = createEventDispatcher();
    function triggerInputChange() {
        dispatch('inputchange');
    }

    $: disabledRowDuplicate =
        fieldBlocks[handle].add === false ||
        (typeof fieldBlocks[handle].maxCount === 'number' &&
            fieldBlocks[handle].maxCount > 0 &&
            Array.isArray(storage[storageKey]) &&
            fieldBlocks[handle].maxCount <= storage[storageKey].length);
    /**
     * 行を上に移動する。
     * @param {number} rowIdx
     */
    function rowUp(rowIdx) {
        const array = storage[storageKey];
        [array[rowIdx - 1], array[rowIdx]] = [array[rowIdx], array[rowIdx - 1]];
        triggerInputChange();
    }

    /**
     * 行を下に移動する。
     * @param {number} rowIdx
     */
    function rowDown(rowIdx) {
        const array = storage[storageKey];
        [array[rowIdx], array[rowIdx + 1]] = [array[rowIdx + 1], array[rowIdx]];
        triggerInputChange();
    }

    /**
     * 行を複製する。
     * @param {number} rowIdx
     */
    function rowDuplicate(rowIdx) {
        const newRow = deepCopy(storage[storageKey][rowIdx]);
        modalConfirm({
            type: 'info',
            title: '確認',
            content: `行を複製しますか？`,
            okButtonLabel: 'OK',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                storage[storageKey].splice(rowIdx + 1, 0, newRow);
                triggerInputChange();
            }
        });
    }

    /**
     * 行を削除する。
     * @param {number} rowIdx
     */
    function rowRemove(rowIdx) {
        modalConfirm({
            type: 'warning',
            title: '確認',
            content: '行を削除しますか？',
            okButtonLabel: '削除',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                storage[storageKey].splice(rowIdx, 1);
                triggerInputChange();
            }
        });
    }

    /**
     * 行を追加する。
     */
    function rowAdd() {
        const newRow = {};
        /**
         * @param {string} handle
         */
        const setNewRow = function (handle) {
            newRow[handle] = deepCopy(fieldBlocks[handle].data);
        };
        fieldBlocks[handle].fields.forEach(setNewRow);
        storage[storageKey].push(newRow);
        triggerInputChange();
    }
</script>

<div data-handle={handle}>
    <div class="mt-table--outline">
        <table id={id} class="table mt-table">
            <thead>
                <tr>
                    {#each fieldBlocks[handle].fields as handleInFields}
                        <th scope="col">
                            {fieldBlocks[handleInFields].label}
                            {#if fieldBlocks[handleInFields].required}
                                <span class="badge badge-danger">{mtappVars.translate['Required']}</span>
                            {/if}
                        </th>
                    {/each}
                    <th scope="col">&nbsp;</th>
                </tr>
            </thead>
            <tbody>
                {#each storage[storageKey] as row, rowIdx (id + rowIdx)}
                    <tr>
                        {#each fieldBlocks[handle].fields as handleInFields, fieldIdx (id + rowIdx + fieldIdx)}
                            <td class="p-2">
                                {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'splitField', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(fieldBlocks[handleInFields].type)}
                                    <MultiFieldProInput
                                        mtappVars={mtappVars}
                                        id={`${id}-${handleInFields}-${rowIdx}-${fieldIdx}`}
                                        storage={row}
                                        storageKey={handleInFields}
                                        handle={handleInFields}
                                        fieldBlock={fieldBlocks[handleInFields]}
                                        on:inputchange={triggerInputChange} />
                                {:else if ['asset', 'image'].includes(fieldBlocks[handleInFields].type)}
                                    <MultiFieldProAsset
                                        mtappVars={mtappVars}
                                        id={`${id}-${handleInFields}-${rowIdx}-${fieldIdx}`}
                                        storage={row}
                                        storageKey={handleInFields}
                                        handle={handleInFields}
                                        fieldBlock={fieldBlocks[handleInFields]}
                                        on:inputchange={triggerInputChange} />
                                {/if}
                            </td>
                        {/each}
                        <td class="p-2">
                            <div class="btn-group btn-group-sm" role="group">
                                <button
                                    class="btn btn-default p-1"
                                    type="button"
                                    tabindex="-1"
                                    disabled={rowIdx === 0}
                                    on:click={() => rowUp(rowIdx)}
                                    aria-label="Move Up">
                                    <svg role="img" class="mt-icon mr-0 top-0">
                                        <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_tri-up'} />
                                    </svg>
                                </button>
                                <button
                                    class="btn btn-default p-1"
                                    type="button"
                                    tabindex="-1"
                                    disabled={Array.isArray(storage[storageKey]) && rowIdx === storage[storageKey].length - 1}
                                    on:click={() => rowDown(rowIdx)}
                                    aria-label="Move Down">
                                    <svg role="img" class="mt-icon mr-0 top-0">
                                        <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_tri-down'} />
                                    </svg>
                                </button>
                                <button
                                    class="btn btn-default p-1"
                                    type="button"
                                    tabindex="-1"
                                    disabled={disabledRowDuplicate}
                                    on:click={() => rowDuplicate(rowIdx)}
                                    aria-label="Duplicate">
                                    <svg role="img" class="mt-icon mr-0 top-0">
                                        <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_duplicate'} />
                                    </svg>
                                </button>
                                <button
                                    class="btn btn-default p-1"
                                    type="button"
                                    tabindex="-1"
                                    disabled={storage[storageKey].length <= 1}
                                    on:click={() => rowRemove(rowIdx)}
                                    aria-label="Remove">
                                    <svg role="img" class="mt-icon mr-0 top-0">
                                        <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_trash'} />
                                    </svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
    {#if fieldBlocks[handle].add !== false && (typeof fieldBlocks[handle].maxCount !== 'number' || (fieldBlocks[handle].maxCount > 0 && Array.isArray(storage[storageKey]) && fieldBlocks[handle].maxCount > storage[storageKey].length))}
        <p class="position-relative d-flex flex-row-reverse mtapp-add-row-button">
            <button class="btn btn-default mt-3 d-flex align-items-center" type="button" on:click={rowAdd}>
                <svg role="img" class="mt-icon">
                    <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_add'} />
                </svg>
                {mtappVars.translate['Add a row']}
            </button>
        </p>
    {/if}
</div>
